import MeService from '@/core/shared/me/me.service';
import { useMessagingStore } from '../messaging/messaging.store';
import { clone } from '@/utils/objects/clone.util';
import { useSearchStore } from './search.store';
import type { SearchGatedUserProfile } from './types/search-gated-user-profile.type';
import type { SelectedFilter } from './types/selected-filter.type';
import { SearchPersistence } from './search.persistence';
import type { PlacePrediction } from './types/place-prediction.type';
import type { PaginatedResponse } from './types/paginated-response.type';
import type { RecentSearch } from './types/recent-search.type';
import { ViewedCandidateService } from '../viewed-candidates/viewed-candidate.service';
import type { DefaultPagination } from './types/default-pagination.type';
import { defaultPagination } from './consts/default-pagination.const';
import { defaultSearchQuery } from './consts/default-seach-query.const';
import { UI_PAGINATION_CAP } from './consts/max-result-window.const';
import { SelectedFilterType } from './types/selected-filter-type.type';
import type { SearchMode } from './types/search-mode.type';
import type { JobLocationCoordinates } from '@/core/shared/job/job.persistence';
import { SearchTab } from './types/search-tab.enum';
import { type Project } from '@factoryfixinc/ats-interfaces';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';
import { ErrorService } from '@/core/shared/errors/error.service';

export class SearchService {
  private viewedCandidateService = new ViewedCandidateService();
  private store = useSearchStore();
  private persistence = new SearchPersistence();
  public get candidates(): SearchGatedUserProfile[] {
    return this.store.candidates;
  }

  public set candidates(candidates: SearchGatedUserProfile[]) {
    this.store.candidates = candidates;
  }

  public get displayedCandidates(): SearchGatedUserProfile[] {
    return this.store.displayedCandidates;
  }

  public patchDoNotContact(employerId: number, userProfileId: number, active: boolean): void {
    if (new MeService().employer?.id !== employerId) return;
    for (const candidate of this.store.candidates) {
      if (Number(candidate.profileId) === userProfileId) candidate.doNotContact = active;
    }
    for (const recipient of useMessagingStore().selectedProUserProfiles) {
      if (recipient.id === userProfileId) recipient.doNotContact = active;
    }
  }

  public get activeTab(): SearchTab {
    return this.store.activeTab;
  }

  public set activeTab(tab: SearchTab) {
    this.store.activeTab = tab;
  }

  public get serverItems(): number | undefined {
    return this.store.serverItems;
  }

  public get paginationItemsLength(): number | undefined {
    return this.store.paginationItemsLength;
  }

  public get totalPages(): number | undefined {
    return this.store.totalPages;
  }

  public get pagination(): DefaultPagination {
    return this.store.pagination;
  }

  public set pagination(pagination: DefaultPagination) {
    this.store.pagination = pagination;
  }

  public get selectedFilters(): SelectedFilter[] {
    return this.store.selectedFilters;
  }

  public set selectedFilters(filters: SelectedFilter[]) {
    this.store.selectedFilters = filters;
  }

  public get searchMode(): SearchMode | undefined {
    return this.store.searchMode;
  }

  public set searchMode(searchMode: SearchMode | undefined) {
    this.store.searchMode = searchMode;
  }

  public get source(): 'typed' | 'recent' {
    return this.store.source;
  }

  public set source(source: 'typed' | 'recent') {
    this.store.source = source;
  }

  public get parentSessionId(): string | null {
    return this.store.parentSessionId;
  }

  public set parentSessionId(parentSessionId: string | null) {
    this.store.parentSessionId = parentSessionId;
  }

  public get searchSessionId(): string | undefined {
    return this.store.searchSessionId;
  }

  public set searchSessionId(searchSessionId: string | undefined) {
    this.store.searchSessionId = searchSessionId;
  }

  public get suppressInferredTypes(): SelectedFilterType[] | undefined {
    return this.store.suppressInferredTypes;
  }

  public set suppressInferredTypes(suppressInferredTypes: SelectedFilterType[] | undefined) {
    this.store.suppressInferredTypes = suppressInferredTypes;
  }

  // Smart searches ('natural' and 'job') rank candidates by document fit rather
  // than the recruiter's literal keywords. The result cards read this to switch on
  // the "Top match" pill and the resume-highlight rule. Keyword/Boolean and
  // legacy (unset) searches are not smart.
  public get isSmartSearch(): boolean {
    return this.store.searchMode === 'natural' || this.store.searchMode === 'job';
  }

  public get jobId(): number | undefined {
    return this.store.jobId;
  }

  public set jobId(jobId: number | undefined) {
    this.store.jobId = jobId;
  }

  public get queryMode(): 'keyword' | 'document' | undefined {
    return this.store.queryMode;
  }

  public set queryMode(queryMode: 'keyword' | 'document' | undefined) {
    this.store.queryMode = queryMode;
  }

  public get documentSearchJobTitle(): string | undefined {
    return this.store.documentSearchJobTitle;
  }

  public set documentSearchJobTitle(documentSearchJobTitle: string | undefined) {
    this.store.documentSearchJobTitle = documentSearchJobTitle;
  }

  public get jobLocationCoordinates(): JobLocationCoordinates | undefined {
    return this.store.jobLocationCoordinates;
  }

  public set jobLocationCoordinates(jobLocationCoordinates: JobLocationCoordinates | undefined) {
    this.store.jobLocationCoordinates = jobLocationCoordinates;
  }

  public removeFilter(type: SelectedFilterType): void {
    this.store.selectedFilters = this.store.selectedFilters.filter(
      (filter) => filter.type !== type,
    );
    TrackingService.trackAction(TrackingActionName.CANDIDATE_FILTER, {
      addedFilter: null,
      removedFilter: type,
      filters: this.store.selectedFilters,
    });
  }

  public updateFilter(filter: SelectedFilter): void {
    this.store.selectedFilters = this.store.selectedFilters.map((item) =>
      item.type === filter.type ? filter : item,
    );
    TrackingService.trackAction(TrackingActionName.CANDIDATE_FILTER, {
      added_filter: filter,
      filters: this.store.selectedFilters,
      removed_filter: null,
    });
  }

  public resetPagination(): void {
    const pagination = clone(defaultPagination);

    this.store.pagination = {
      ...pagination,
      // keep user modified itemsPerPage
      itemsPerPage: this.store.pagination.itemsPerPage,
    };
  }

  public resetResults(): void {
    this.store.candidates = [];
    this.store.serverItems = undefined;
    this.store.paginationItemsLength = undefined;
    this.store.totalPages = undefined;
    this.store.searchSessionId = undefined;
    this.store.locationSearchInput = '';
    this.resetPagination();
  }

  public resetSearch(): void {
    this.store.searchQuery = clone(defaultSearchQuery);
  }

  public setSelectedFiltersFromProject(project?: Project): void {
    this.store.selectedFilters = [];
    this.store.selectedFiltersId = undefined;

    const savedSearch = project?.savedSearches?.[0];
    const selectedFilters: SelectedFilter[] = [];

    if (!savedSearch) {
      return;
    }

    this.store.selectedFiltersId = savedSearch.id;

    if (savedSearch.query) {
      selectedFilters.push({
        type: SelectedFilterType.KEYWORD,
        value: savedSearch.query,
      });
    }

    if (savedSearch.location) {
      selectedFilters.push({
        type: SelectedFilterType.LOCATION,
        value: savedSearch.location,
      });
    }

    if (savedSearch.aroundRadius) {
      selectedFilters.push({
        type: SelectedFilterType.AROUND_RADIUS,
        value: savedSearch.aroundRadius,
      });
    }

    const brand = savedSearch.savedSearchFilters?.find((filter) => filter.type === 'BRAND');

    if (brand) {
      selectedFilters.push({
        type: SelectedFilterType.BRAND,
        value: brand.value,
      });
    }

    const jobTitle = savedSearch.savedSearchFilters?.find((filter) => filter.type === 'JOB_TITLE');

    if (jobTitle) {
      selectedFilters.push({
        type: SelectedFilterType.JOB_TITLE,
        value: jobTitle.value,
      });
    }

    const knowledgeDiscipline = savedSearch.savedSearchFilters?.find(
      (filter) => filter.type === 'KNOWLEDGE_DISCIPLINE',
    );

    if (knowledgeDiscipline) {
      selectedFilters.push({
        type: SelectedFilterType.KNOWLEDGE_DISCIPLINE,
        value: knowledgeDiscipline.value,
      });
    }

    const lastActive = savedSearch.savedSearchFilters?.find(
      (filter) => filter.type === 'LAST_ACTIVE',
    );

    if (lastActive) {
      selectedFilters.push({
        type: SelectedFilterType.LAST_ACTIVE,
        value: Number(lastActive.value),
      });
    }

    const technology = savedSearch.savedSearchFilters?.find(
      (filter) => filter.type === 'TECHNOLOGY',
    );

    if (technology) {
      selectedFilters.push({
        type: SelectedFilterType.TECHNOLOGY,
        value: technology.value,
      });
    }

    const yearsOfExperience =
      savedSearch.savedSearchFilters?.filter((filter) => filter.type === 'YEARS_OF_EXPERIENCE') ??
      [];

    if (yearsOfExperience.length) {
      selectedFilters.push({
        type: SelectedFilterType.YEARS_OF_EXPERIENCE,
        value: yearsOfExperience.map((filter) => Number(filter.value)),
      });
    }

    this.store.selectedFilters = selectedFilters;
  }

  public getAddressPredictions(address: string): Promise<PlacePrediction[]> {
    return this.persistence.getAddressPredictions(address);
  }

  public getRecentSearches(): Promise<RecentSearch[]> {
    return this.persistence.getRecentSearches();
  }

  public async searchCandidates(options?: {
    // Freshness gate (a search-flight token supplied by the caller). When a clear or a newer
    // search superseded this request while it was in flight, the predicate returns false and
    // the stale response is NOT written to the store — so it can't overwrite the newer
    // results. The response is still returned for the caller to inspect. Callers that omit it
    // (legacy/project/tag paths) always commit.
    shouldCommitResults?: () => boolean;
    clearSelectedCandidatesOnSessionChange?: boolean;
  }): Promise<PaginatedResponse<SearchGatedUserProfile>> {
    const result = await this.persistence.searchCandidates();

    const hitsPerPage = result.hitsPerPage || defaultPagination.itemsPerPage;
    const maxPages = Math.floor(UI_PAGINATION_CAP / hitsPerPage);

    // The displayed count can read higher than what we let users page through — navigation
    // caps at UI_PAGINATION_CAP so the paginator never offers a page past what the backend
    // serves (ONE-6438).
    result.nbPages = Math.min(result.nbPages, maxPages);

    // Superseded mid-flight: return the data without touching the store, so a stale response
    // can't overwrite the newer search's results.
    if (options?.shouldCommitResults && !options.shouldCommitResults()) {
      return result;
    }

    const messagingStore = useMessagingStore();
    if (
      options?.clearSelectedCandidatesOnSessionChange &&
      messagingStore.selectedProUserProfiles.length > 0 &&
      this.store.searchSessionId !== result.searchSessionId
    ) {
      // Selections belong to the session that produced them. Do not let a new session
      // returned after the repeat window attribute those candidates to the new session.
      messagingStore.selectedProUserProfiles = [];
    }

    this.store.candidates = result.hits;
    // Unlock/search refreshes must also update recipients selected from older hits.
    const annotations = new Map(
      result.hits.map((hit) => [Number(hit.profileId), hit.doNotContact]),
    );
    for (const recipient of messagingStore.selectedProUserProfiles) {
      const doNotContact = annotations.get(recipient.id);
      if (doNotContact !== undefined) recipient.doNotContact = doNotContact;
    }
    this.store.serverItems = result.nbHits;
    this.store.paginationItemsLength = Math.min(result.nbHits, maxPages * hitsPerPage);
    this.store.totalPages = result.nbPages;
    this.store.searchSessionId = result.searchSessionId;

    // Supplemental; a failure here must not discard the primary search result.
    try {
      await this.viewedCandidateService.fetchViewedCandidates();
    } catch (error) {
      ErrorService.captureException(error);
    }

    return result;
  }
}
